import type { i18n, InitOptions } from 'i18next';
import i18next from 'i18next';

import { LocaleEnum } from '@/helpers/enums/locale.enum.ts';

export const getInitialLanguage = (): string => {
  const url = window.location.href;
  const language = url.split('language=')[1];
  return language === LocaleEnum.ARABIC || language === LocaleEnum.ENGLISH
    ? language
    : localStorage.getItem(LocaleEnum.LOCALE_STORAGE_KEY) || LocaleEnum.ENGLISH;
};

export const initializeI18n = async (
  initialLanguage: string
): Promise<i18n> => {
  const defaultTranslations = {
    sidemenu: {
      supportTickets: 'Support Tickets',
      internalTeam: 'Internal Training',
      internalTeamBadge: 'New',
      onboardingSteps: 'Onboarding Steps',
    },
    shared: {
      switchToSuperAdmin: 'Switch to Super Admin',
    },
    // KP-61 — department-manager labels on the learning-groups surface.
    // The CMS bundle owns the other teams.* keys; these new ones survive
    // the deep merge (same convention as adminDashboard.watar.*).
    teams: {
      manager: 'Manager',
      setManager: 'Set manager',
      setAsManager: 'Set as manager',
      clearManager: 'Clear manager',
      currentManager: 'Current manager',
      noManager: 'No manager assigned',
    },
    // KP-1 Watar dashboard — dedicated namespace (adminDashboard.watar.*).
    // The CMS bundle deep-merges over these seeds at runtime; keys the CMS
    // does not know (this whole subtree) survive the merge.
    adminDashboard: {
      watar: {
        title: 'Dashboard',
        subtitle:
          'Live completion metrics across every module — assigned, active, finished, and where people drop off.',
        notAvailable: '—',
        loadFailed: 'Failed to load module metrics.',
        retry: 'Retry',
        empty: 'No published modules yet.',
        tiles: {
          totalLearners: 'Learners assigned',
          inProgress: 'Active enrollments',
          modules: 'Modules',
          overallCompletion: 'Overall completion',
        },
        modulesTable: {
          title: 'Completion by module',
          bestPerformance: 'Best performance',
          columns: {
            module: 'Module',
            items: 'Items',
            assigned: 'Assigned',
            completed: 'Completed',
            completion: 'Completion',
          },
        },
        // KP-60 — chart labels for the consolidated dashboard.
        charts: {
          completionPercent: 'Completion %',
          completionTooltip: '{{completed}}/{{assigned}} completed • {{percent}}%',
        },
        dropoff: {
          assigned: 'Assigned: {{count}}',
          started: 'Started: {{count}}',
          maxDrop: 'Biggest drop: {{step}} (-{{count}})',
          chartStarted: 'Started',
          empty: 'This module has no items yet.',
          loadFailed: 'Failed to load the drop-off funnel.',
          viewLearners: 'View learners',
          columns: {
            step: 'Step',
            completed: 'Completed',
            rate: 'Rate of starters',
            drop: 'Drop',
          },
        },
        recent: {
          title: 'Recent completions',
          empty: 'No completions yet.',
          loadFailed: 'Failed to load recent completions.',
          neverActive: 'Never',
          columns: {
            learner: 'Learner',
            module: 'Training',
            status: 'Status',
            progress: 'Progress',
            lastActivity: 'Last activity',
          },
          statuses: {
            NOT_STARTED: 'Not Started',
            IN_PROGRESS: 'In Progress',
            PASS_WITH_CERTIFICATE: 'Completed • Certificate',
            PASS_WITHOUT_CERTIFICATE: 'Completed',
            FAILED: 'Failed',
          },
        },
        // KP-61 — the department manager's "My departments" section.
        myDepartments: {
          title: 'My department',
          membersCount: '{{count}} members',
          loadFailed: 'Failed to load department members.',
          empty: 'No members in this department yet.',
          noModules: 'No modules assigned.',
          completed: 'Completed',
          columns: {
            member: 'Member',
            modulesCompleted: 'Modules completed',
            progress: 'Average progress',
          },
          moduleColumns: {
            module: 'Module',
            items: 'Items completed',
            progress: 'Progress',
          },
        },
      },
    },
    internalTeam: {
      tabs: {
        overview: 'Overview',
        engagement: 'Engagement',
      },
      empty: 'Nothing here yet.',
      comingSoon:
        'Coming soon \u2014 this area is under active development and will be enhanced continuously.',
      modulesAtAGlance: '{{coursesText}} at a glance',
      totalModules: 'Total {{coursesText}}',
      totalLearners: 'Total {{studentsText}}',
      loadFailed: 'Failed to load {{coursesText}}.',
      retry: 'Retry',
      engagement: {
        title: '{{coursesText}} engagement',
        totalEnrollees: 'Total enrollments',
        completedCourses: '{{coursesText}} completed',
        averagePerUser: 'Average {{coursesText}} per {{studentText}}',
        topCompleted: 'Top 5 completed {{coursesText}}',
        mostDroppedOff: 'Most dropped-off {{courseText}}',
        columns: {
          completed: 'Completed',
          rate: 'Rate',
        },
        inProgressOfEnrolled: '{{count}} of {{total}} in progress',
        // KP-60 — the enrollment-status donut. The engagement source is a
        // snapshot (no time series), so a distribution is what gets drawn.
        statusDistribution: {
          title: 'Enrollment status distribution',
          subtitle: 'Enrollments',
          notStarted: 'Not started',
          inProgress: 'In progress',
          completed: 'Completed',
          failed: 'Failed',
        },
        noDropOff: 'No {{coursesText}} currently have learners in progress.',
        dropOffDefinition:
          'Drop-off = learners who started the {{courseText}} but have not finished (in progress \u00f7 total enrolled).',
        notAvailable: '\u2014',
        drawer: {
          // KP-54 \u2014 scope label used in place of a course name when a
          // stat-tile drilldown spans every module.
          scopeAllModules: 'All {{coursesText}}',
          title: 'Learners \u2014 {{courseName}}',
          loadFailed: 'Failed to load learners.',
          retry: 'Retry',
          empty: 'No learners match this view.',
          close: 'Close',
          columns: {
            name: 'Name',
            email: 'Email',
            status: 'Status',
            progress: 'Progress',
            lastActivity: 'Last activity',
          },
          statuses: {
            NOT_STARTED: 'Not Started',
            IN_PROGRESS: 'In Progress',
            PASS_WITH_CERTIFICATE: 'Completed',
            PASS_WITHOUT_CERTIFICATE: 'Completed',
            FAILED: 'Failed',
          },
          neverActive: 'Never',
        },
      },
    },
    journeyPlanner: {
      createCourse: 'Create {{courseText}}',
      createNewCourse: '+ Create new {{courseText}}',
    },
    // KP-62 — interlocking journeys: prerequisite picker on the Module
    // Planner form. Dedicated namespace so the CMS bundle merge cannot
    // collide with it (same pattern as moduleTests.*).
    journeyPrerequisite: {
      label: 'Prerequisite module',
      hint: 'Learners must complete the selected module before this one unlocks. Clear it to remove the requirement.',
      noOptions: 'No other modules exist yet.',
    },
    // Friendly texts for the server's prerequisite-validation rejections;
    // the toaster resolves keys under `messages.`, so they live here.
    messages: {
      journeyPrerequisite: {
        selfReference: 'A module cannot be its own prerequisite.',
        cycle:
          'This prerequisite would create a loop between modules — pick a different module.',
        notFound:
          'The selected prerequisite module no longer exists in this workspace.',
      },
    },
    // KP-12 — module version history in the Module Planner. Dedicated
    // namespace so the CMS bundle merge cannot collide with it (same
    // pattern as moduleTests.*).
    moduleHistory: {
      tab: 'History',
      tabTitle: 'Module History',
      title: 'Version history',
      subtitle:
        'A snapshot is saved automatically every time this module or its items change. Restore any version to bring its content back.',
      empty: 'No versions yet — edits to this module will appear here.',
      columns: {
        when: 'When',
        who: 'By',
        reason: 'Change',
        items: 'Items',
        actions: 'Actions',
      },
      reasonUpdate: 'Edit',
      reasonRestore: 'Restore',
      unknownAuthor: 'Deleted user',
      view: 'View',
      restore: 'Restore',
      detailsTitle: 'What restoring this version changes',
      noDifferences: 'This version matches the current content.',
      nameChange: 'Module name: "{{from}}" → "{{to}}"',
      itemCounts: 'Items in this version: {{then}} (currently {{now}})',
      restoredItems: 'Items that will be brought back',
      removedItems: 'Items that will be removed',
      renamedItems: 'Items that will be renamed',
      confirmTitle: 'Restore this version?',
      confirmMessage:
        'Restoring replaces the module’s current name, settings and items with this version. Items added since this version will be removed.',
      confirmHint:
        'A snapshot of the current content is saved first, so you can undo this restore from the history.',
    },
    // KP-6 — test authoring inside the Module Planner. Dedicated namespace
    // so the CMS bundle merge cannot collide with it (same pattern as
    // adminDashboard.watar.*).
    moduleTests: {
      title: 'Tests',
      subtitle:
        'Tests for the linked {{courseText}} — authored in the {{courseText}} quiz builder.',
      addTest: 'Add test',
      noCourseTooltip:
        'Link a {{courseText}} to this item first — tests belong to the linked {{courseText}}.',
      noCourseHint:
        'This item has no linked {{courseText}} yet. Pick "{{courseText}} completion" and choose a {{courseText}} to manage its tests here.',
      empty: 'No tests yet for this {{courseText}}. Add the first one.',
      loadFailed: 'Failed to load tests.',
      retry: 'Retry',
      edit: 'Edit',
      columns: {
        name: 'Test',
        createdAt: 'Created',
      },
    },
    onboarding: {
      widgetTitle: 'Onboarding Journey',
      loadFailed: 'Failed to load onboarding progress.',
      retry: 'Retry',
      empty: 'No onboarding data yet.',
      complete: 'Complete',
      inProgress: 'In Progress',
      notStarted: 'Not Started',
      mostStuckStep: 'Most users are stuck on: {{step}}',
      columnHeader: 'Onboarding',
      cellComplete: '✓ Complete',
      cellInProgress: 'Step {{completed}}/{{total}} ({{step}})',
      panelTitle: 'Onboarding',
      stepKey: 'Step',
      stepStatus: 'Status',
      stepCompletedAt: 'Completed At',
      stepSummary: 'Summary',
      stepStatuses: {
        NOT_STARTED: 'Not Started',
        INCOMPLETE: 'Not Started',
        COMPLETED: 'Completed',
        SKIPPED: 'Skipped',
      },
      tour: {
        skip: 'Skip tour',
        showAgain: 'Show tour again',
        finishCta: 'Create your first module',
        welcomeTitle: 'Your new portal',
        welcomeBody: 'Let us show you around.',
        journeyNavTitle: 'Module Planner',
        journeyNavBody: 'Click here to open the Module Planner.',
        journeyCtaTitle: 'Add your first module',
        journeyCtaBody:
          'Create your first module here — add videos, documents and a quiz, in the order you want.',
        usersColumnTitle: 'Track onboarding',
        usersColumnBody:
          "Track every intern and new joiner's onboarding, step by step.",
        widgetTitle: 'Onboarding at a glance',
        widgetBody:
          "See who finished, who's in progress, and where people get stuck.",
        finishTitle: "You're ready",
        finishBody: "You're ready — create your first module.",
      },
    },
    onboardingSteps: {
      pageTitle: 'Onboarding Steps',
      loadFailed: 'Failed to load onboarding steps.',
      retry: 'Retry',
      empty: 'No onboarding steps yet.',
      addStep: 'Add step',
      columnOrder: 'Order',
      columnKey: 'Key',
      columnType: 'Type',
      columnActive: 'Active',
      columnSkippable: 'Skippable',
      columnActions: 'Actions',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      editTitle: 'Edit step',
      createTitle: 'Add step',
      keyLabel: 'Key',
      keyHelper:
        'A short, unique, machine-readable identifier (e.g. welcome-video).',
      typeLabel: 'Type',
      roleFilterLabel: 'Role filter (optional)',
      roleFilterHelper:
        'Leave empty to apply to every role. Press enter to add a role.',
      videoTitleEn: 'Title (English)',
      videoTitleAr: 'Title (Arabic)',
      videoUrl: 'Video URL',
      videoDurationHintEn: 'Duration hint (English)',
      videoDurationHintAr: 'Duration hint (Arabic)',
      videoDisclaimerEn: 'Disclaimer (English)',
      videoDisclaimerAr: 'Disclaimer (Arabic)',
      departments: 'Departments',
      addDepartment: 'Add department',
      departmentKey: 'Key',
      departmentNameEn: 'Name (English)',
      departmentNameAr: 'Name (Arabic)',
      departmentVideoUrl: 'Video URL',
      removeDepartment: 'Remove department',
      cards: 'Cards',
      addCard: 'Add card',
      cardTitleEn: 'Card title (English)',
      cardTitleAr: 'Card title (Arabic)',
      cardBodyEn: 'Card body (English)',
      cardBodyAr: 'Card body (Arabic)',
      removeCard: 'Remove card',
      requiresSignature: 'Requires signature',
      groups: 'Groups',
      addGroup: 'Add group',
      groupNameEn: 'Group name (English)',
      groupNameAr: 'Group name (Arabic)',
      removeGroup: 'Remove group',
      requiresAcceptancePerGroup: 'Requires acceptance per group',
      passMark: 'Pass mark',
      examId: 'Exam ID',
      examIdHelper: 'Optional — the numeric ID of the linked exam/assessment.',
      jsonConfig: 'Config (JSON)',
      jsonInvalid: 'Invalid JSON.',
      jsonHelper:
        'This step type has no dedicated form yet — edit its raw config as JSON.',
      gradingEnabled: 'Graded exam (requires a pass mark)',
      gradingPassMark: 'Pass mark (%)',
      gradingHelper:
        'Learners must score at least this percentage to complete the step. Retakes are always allowed.',
      assessments: 'Questions (assessments)',
      assessmentsHelper:
        'Select the assessments that make up this step, in order.',
      assessmentsLoadFailed: 'Failed to load assessments.',
      correctAnswers: 'Correct answers',
      correctAnswersHelper:
        'Mark the correct option(s) for each selected assessment — submissions are graded against these on the server.',
      noKeyMarked:
        'No correct option marked — this question will not be graded.',
      saveAnswerKey: 'Save correct answers',
      answerKeySaved: 'Correct answers saved.',
      answerKeySaveFailed: 'Failed to save correct answers.',
      journeyChecklist: 'Journey',
      journeyChecklistHelper:
        'The journey (checklist) this step embeds — the step completes only when that journey is complete for the learner.',
      journeysLoadFailed: 'Failed to load journeys.',
      // KP-54 — quiz wiring: pick the exam from a course's quiz list.
      quizCourse: 'Course',
      quizCourseHelper: 'Pick the course whose quizzes you want to link.',
      quizExam: 'Quiz',
      quizExamHelper:
        'Only quizzes authored in the course Quiz builder are listed.',
      quizzesLoadFailed: 'Failed to load quizzes.',
      quizzesEmpty:
        'This course has no quizzes yet — author one in the course Quiz builder first.',
      coursesLoadFailed: 'Failed to load courses.',
      quizCurrentExam: 'Currently linked quiz id: {{examId}}.',
      // KP-54 — per-question display labels (ASSESSMENT steps).
      questionLabels: 'Question labels',
      questionLabelsHelper:
        'Optional label shown above each question — provide both English and Arabic.',
      questionLabelEn: 'Label (English)',
      questionLabelAr: 'Label (Arabic)',
      // KP-57 — AI-assisted step authoring.
      copyPrompt: 'Copy AI authoring prompt',
      promptCopied: 'Authoring prompt copied to clipboard.',
      pasteJson: 'Paste JSON',
      pasteJsonTitle: 'Paste step config JSON',
      pasteJsonHelper:
        'Paste the JSON produced from the authoring prompt. It only fills the form below — review it, then save.',
      pasteJsonLabel: 'Config JSON',
      pasteApply: 'Apply to form',
      pasteInvalidJson:
        'Not valid JSON — paste the raw JSON object only, without markdown fences or extra text.',
      pasteWrongShape:
        'Valid JSON, but it does not match the {{type}} step config.',
      configApplied: 'Config applied to the form — review it, then save.',
    },
    // KP-7 — per-user Results tab in the admin user profile (internal-team
    // mode). Same seed-survives-CMS-merge strategy as the namespaces above.
    userResults: {
      tab: 'Results',
      loadFailed: 'Failed to load results.',
      retry: 'Retry',
      empty: 'No {{coursesText}} enrollments yet.',
      notAvailable: '—',
      notGradedYet: 'Not graded yet',
      neverActive: 'Never',
      columns: {
        module: 'Training',
        status: 'Status',
        progress: 'Progress',
        score: 'Score',
        lastActivity: 'Last activity',
      },
      statuses: {
        NOT_STARTED: 'Not Started',
        IN_PROGRESS: 'In Progress',
        PASS_WITH_CERTIFICATE: 'Completed • Certificate',
        PASS_WITHOUT_CERTIFICATE: 'Completed',
        FAILED: 'Failed',
      },
    },
    watarRoles: {
      title: 'Simplified roles',
      description:
        'This workspace uses four roles: Watar Admin, Watar New Joiner, Watar Intern and Watar Employee. Create any missing role and apply its baseline permissions — existing roles and custom permissions are never removed.',
      setupRoles: 'Set up roles',
      syncRoles: 'Re-apply baselines',
      done: 'Roles are ready.',
      failed: 'Role setup failed — please try again.',
    },
    // KP-14 — bulk actions on the Users page. New subtree: the CMS bundle
    // does not know these keys, so they survive the deep merge (same
    // convention as adminDashboard.watar.*).
    users: {
      bulk: {
        selectedCount: '{{count}} selected',
        capExceeded: 'You can act on at most {{max}} users at once.',
        assignRole: 'Assign role',
        addToTeam: 'Add to team',
        removeFromTeam: 'Remove from team',
        addToDepartment: 'Add to department',
        removeFromDepartment: 'Remove from department',
        selectGroup: 'Select a group',
        clearSelection: 'Clear selection',
        confirmMessage: 'This will apply to {{count}} selected user(s).',
        allSucceeded: '{{count}} user(s) updated successfully',
        resultsSummary: '{{succeeded}} succeeded, {{failed}} failed',
        failureDetails: 'Failure details',
        close: 'Close',
        errors: {
          notInOrganization: 'User is not part of this organization',
          userLimitExceeded: 'Organization user limit reached',
          updateFailed: 'Update failed',
        },
      },
    },
    // KP-56/KP-59 — AI Assistant block (keep as ONE delimited subtree)
    aiAssistant: {
      settings: {
        title: 'AI Assistant',
        description:
          "Connect the organization's completion provider. The key is stored encrypted and is only ever shown masked — it is never sent back to the browser.",
        provider: 'Provider',
        openAiCompatible: 'OpenAI-compatible endpoint',
        apiKey: 'API key',
        baseUrl: 'Base URL',
        model: 'Model',
        modelOptional: 'Optional — leave empty for the provider default.',
        configured: 'Configured • {{keyMasked}}',
        save: 'Save',
        clear: 'Clear',
        cancel: 'Cancel',
        clearConfirmTitle: 'Remove the stored credential?',
        clearConfirmBody:
          'Assistant features that depend on this provider will stop working for this organization until a new key is saved.',
        errors: {
          required: 'This field is required.',
          tooShort: 'The key looks too short.',
        },
      },
      insights: {
        title: 'Insights',
        generate: 'Generate insights',
        refresh: 'Refresh insights',
        intro:
          'Get a quick highlights summary of module completion and drop-off — computed on demand from the dashboard data.',
        caption:
          "Computed from the dashboard's current completion and drop-off data.",
        loadFailed: 'Could not compute insights — please try again.',
        empty:
          'Not enough data yet — insights appear once modules have assigned learners.',
        highlights: {
          overall:
            '{{modules}} active modules — {{percent}}% overall completion.',
          atRisk:
            'Most at risk: "{{name}}" at {{percent}}% completion across {{assigned}} assigned learners.',
          biggestDrop:
            'Biggest drop in "{{name}}": {{count}} learners stop at "{{step}}".',
          best: 'Best performer: "{{name}}" at {{percent}}% completion.',
        },
      },
    },
    supportTickets: {
      detailsTitle: 'Ticket Details',
      unknown: 'Unknown',
      dueDate: 'Due',
      statusLabel: 'Update Status',
      updateStatus: 'Update',
      assignees: 'Assignees',
      description: 'Description',
      addComment: 'Add Comment',
      submitComment: 'Submit',
      comments: 'Comments',
      noComments: 'No comments yet.',
      unknownAuthor: 'Unknown author',
      noTaskSelected: 'Select a ticket to view details.',
      noListsConfigured:
        'No ClickUp lists are configured for this organization.',
      retry: 'Retry',
      tasksCount: '{{count}} tasks',
      completedTasksCount: '{{count}} completed',
      showCompletedTasks: 'Show completed tasks',
      showTasks: 'Show tasks',
      noTasks: 'No tasks found in this list.',
      noCompletedTasks: 'No completed tasks found.',
      toggleShowCompleted: 'Show completed tasks',
      createdAt: 'Created',
      updatedAt: 'Last updated',
      completed: 'Completed',
      closed: 'Closed',
      loadMore: 'Load 10 more',
    },
  };
  // Arabic seeds for the new Internal Team surface. Every other key keeps the
  // English seed (same as today); the CMS bundle overrides per language at
  // runtime via addResourceBundle(..., deep=true, overwrite=true).
  const arDefaultTranslations = {
    ...defaultTranslations,
    sidemenu: {
      ...defaultTranslations.sidemenu,
      supportTickets: 'تذاكر الدعم',
      internalTeam: 'التدريب الداخلي',
      internalTeamBadge: 'جديد',
      onboardingSteps: 'خطوات التأهيل',
    },
    shared: {
      ...defaultTranslations.shared,
      switchToSuperAdmin: 'الانتقال إلى مدير النظام',
    },
    // KP-14 — bulk actions on the Users page.
    users: {
      ...defaultTranslations.users,
      bulk: {
        selectedCount: 'تم تحديد {{count}}',
        capExceeded: 'يمكن تنفيذ الإجراء على {{max}} مستخدمًا كحد أقصى في المرة الواحدة.',
        assignRole: 'تعيين الدور',
        addToTeam: 'إضافة إلى فريق',
        removeFromTeam: 'إزالة من فريق',
        addToDepartment: 'إضافة إلى قسم',
        removeFromDepartment: 'إزالة من قسم',
        selectGroup: 'اختر مجموعة',
        clearSelection: 'إلغاء التحديد',
        confirmMessage: 'سيتم تطبيق هذا الإجراء على {{count}} من المستخدمين المحددين.',
        allSucceeded: 'تم تحديث {{count}} من المستخدمين بنجاح',
        resultsSummary: 'نجح {{succeeded}} وفشل {{failed}}',
        failureDetails: 'تفاصيل الإخفاقات',
        close: 'إغلاق',
        errors: {
          notInOrganization: 'المستخدم ليس ضمن هذه المنشأة',
          userLimitExceeded: 'تم بلوغ الحد الأقصى لمستخدمي المنشأة',
          updateFailed: 'فشل التحديث',
        },
      },
    },
    // KP-61 — department-manager labels.
    teams: {
      ...defaultTranslations.teams,
      manager: 'المدير',
      setManager: 'تعيين المدير',
      setAsManager: 'تعيين كمدير',
      clearManager: 'إزالة المدير',
      currentManager: 'المدير الحالي',
      noManager: 'لا يوجد مدير معيّن',
    },
    adminDashboard: {
      ...defaultTranslations.adminDashboard,
      watar: {
        title: 'لوحة المعلومات',
        subtitle:
          'مؤشرات إكمال حية لكل الوحدات — المعيّنون، النشطون، المكتملون، وأين يتعثر الأشخاص.',
        notAvailable: 'غير متاح',
        loadFailed: 'فشل تحميل مؤشرات الوحدات.',
        retry: 'إعادة المحاولة',
        empty: 'لا توجد وحدات منشورة بعد.',
        tiles: {
          totalLearners: 'المتعلمون المعيّنون',
          inProgress: 'الاشتراكات النشطة',
          modules: 'الوحدات',
          overallCompletion: 'نسبة الإكمال الكلية',
        },
        modulesTable: {
          title: 'الإكمال حسب الوحدة',
          bestPerformance: 'الأفضل أداءً',
          columns: {
            module: 'الوحدة',
            items: 'العناصر',
            assigned: 'المعيّنون',
            completed: 'المكتملون',
            completion: 'نسبة الإكمال',
          },
        },
        // KP-60 — chart labels for the consolidated dashboard.
        charts: {
          completionPercent: 'نسبة الإكمال',
          completionTooltip: 'أكمل {{completed}} من {{assigned}} • {{percent}}%',
        },
        dropoff: {
          assigned: 'المعيّنون: {{count}}',
          started: 'بدأوا: {{count}}',
          maxDrop: 'أكبر تسرب: {{step}} (-{{count}})',
          chartStarted: 'بدأوا',
          empty: 'لا تحتوي هذه الوحدة على عناصر بعد.',
          loadFailed: 'فشل تحميل مسار التسرب.',
          viewLearners: 'عرض المتعلمين',
          columns: {
            step: 'الخطوة',
            completed: 'المكتملون',
            rate: 'نسبة من بدأوا',
            drop: 'التسرب',
          },
        },
        recent: {
          title: 'الإكمالات الأخيرة',
          empty: 'لا توجد إكمالات بعد.',
          loadFailed: 'فشل تحميل الإكمالات الأخيرة.',
          neverActive: 'أبداً',
          columns: {
            learner: 'المتعلم',
            module: 'التدريب',
            status: 'الحالة',
            progress: 'التقدم',
            lastActivity: 'آخر نشاط',
          },
          statuses: {
            NOT_STARTED: 'لم يبدأ',
            IN_PROGRESS: 'قيد التقدم',
            PASS_WITH_CERTIFICATE: 'مكتمل • بشهادة',
            PASS_WITHOUT_CERTIFICATE: 'مكتمل',
            FAILED: 'راسب',
          },
        },
        // KP-61 — the department manager's "My departments" section.
        myDepartments: {
          title: 'قسمي',
          membersCount: 'الأعضاء: {{count}}',
          loadFailed: 'فشل تحميل أعضاء القسم.',
          empty: 'لا يوجد أعضاء في هذا القسم بعد.',
          noModules: 'لا توجد وحدات معيّنة.',
          completed: 'مكتمل',
          columns: {
            member: 'العضو',
            modulesCompleted: 'الوحدات المكتملة',
            progress: 'متوسط التقدم',
          },
          moduleColumns: {
            module: 'الوحدة',
            items: 'العناصر المكتملة',
            progress: 'التقدم',
          },
        },
      },
    },
    journeyPlanner: {
      createCourse: 'إنشاء {{courseText}}',
      createNewCourse: '+ إنشاء {{courseText}}',
    },
    journeyPrerequisite: {
      label: 'الوحدة المتطلبة مسبقًا',
      hint: 'يجب على المتعلمين إكمال الوحدة المحددة قبل فتح هذه الوحدة. امسح الاختيار لإزالة الشرط.',
      noOptions: 'لا توجد وحدات أخرى بعد.',
    },
    messages: {
      ...defaultTranslations.messages,
      journeyPrerequisite: {
        selfReference: 'لا يمكن أن تكون الوحدة متطلبًا مسبقًا لنفسها.',
        cycle: 'هذا المتطلب المسبق سيُنشئ حلقة بين الوحدات — اختر وحدة أخرى.',
        notFound:
          'الوحدة المحددة كمتطلب مسبق لم تعد موجودة في مساحة العمل هذه.',
      },
    },
    moduleHistory: {
      tab: 'السجلّ',
      tabTitle: 'سجلّ الوحدة',
      title: 'سجلّ الإصدارات',
      subtitle:
        'يتم حفظ نسخة تلقائيًا في كل مرة تتغير فيها هذه الوحدة أو عناصرها. يمكنك استعادة أي إصدار لإرجاع محتواه.',
      empty: 'لا توجد إصدارات بعد — ستظهر تعديلات هذه الوحدة هنا.',
      columns: {
        when: 'التاريخ',
        who: 'بواسطة',
        reason: 'التغيير',
        items: 'العناصر',
        actions: 'الإجراءات',
      },
      reasonUpdate: 'تعديل',
      reasonRestore: 'استعادة',
      unknownAuthor: 'مستخدم محذوف',
      view: 'عرض',
      restore: 'استعادة',
      detailsTitle: 'ما الذي ستغيّره استعادة هذا الإصدار',
      noDifferences: 'هذا الإصدار مطابق للمحتوى الحالي.',
      nameChange: 'اسم الوحدة: "{{from}}" ← "{{to}}"',
      itemCounts: 'عدد العناصر في هذا الإصدار: {{then}} (حاليًا {{now}})',
      restoredItems: 'عناصر ستتم إعادتها',
      removedItems: 'عناصر ستتم إزالتها',
      renamedItems: 'عناصر ستتم إعادة تسميتها',
      confirmTitle: 'استعادة هذا الإصدار؟',
      confirmMessage:
        'الاستعادة تستبدل اسم الوحدة وإعداداتها وعناصرها الحالية بمحتوى هذا الإصدار. العناصر المضافة بعد هذا الإصدار ستتم إزالتها.',
      confirmHint:
        'يتم حفظ نسخة من المحتوى الحالي أولًا، لذا يمكنك التراجع عن هذه الاستعادة من السجلّ.',
    },
    moduleTests: {
      title: 'الاختبارات',
      subtitle:
        'اختبارات {{courseText}} المرتبطة — يتم إعدادها في منشئ الاختبارات داخل {{courseText}}.',
      addTest: 'إضافة اختبار',
      noCourseTooltip:
        'اربط {{courseText}} بهذا العنصر أولاً — الاختبارات تتبع {{courseText}} المرتبطة.',
      noCourseHint:
        'لا يوجد {{courseText}} مرتبط بهذا العنصر بعد. اختر "إكمال {{courseText}}" ثم حدد {{courseText}} لإدارة اختباراته من هنا.',
      empty: 'لا توجد اختبارات لهذا {{courseText}} بعد. أضف الاختبار الأول.',
      loadFailed: 'فشل تحميل الاختبارات.',
      retry: 'إعادة المحاولة',
      edit: 'تعديل',
      columns: {
        name: 'الاختبار',
        createdAt: 'تاريخ الإنشاء',
      },
    },
    onboarding: {
      widgetTitle: 'رحلة التأهيل',
      loadFailed: 'فشل تحميل بيانات التأهيل.',
      retry: 'إعادة المحاولة',
      empty: 'لا توجد بيانات تأهيل بعد.',
      complete: 'مكتمل',
      inProgress: 'قيد التقدم',
      notStarted: 'لم يبدأ',
      mostStuckStep: 'أكثر المستخدمين توقفوا عند: {{step}}',
      columnHeader: 'التأهيل',
      cellComplete: '✓ مكتمل',
      cellInProgress: 'الخطوة {{completed}}/{{total}} ({{step}})',
      panelTitle: 'التأهيل',
      stepKey: 'الخطوة',
      stepStatus: 'الحالة',
      stepCompletedAt: 'تاريخ الإكمال',
      stepSummary: 'الملخص',
      stepStatuses: {
        NOT_STARTED: 'لم يبدأ',
        INCOMPLETE: 'لم يبدأ بعد',
        COMPLETED: 'مكتمل',
        SKIPPED: 'تم التخطي',
      },
      tour: {
        skip: 'تخطي الجولة',
        showAgain: 'إظهار الجولة مرة أخرى',
        finishCta: 'أنشئ وحدتك الأولى',
        welcomeTitle: 'بوابتك الجديدة',
        welcomeBody: 'دعنا نأخذك في جولة سريعة.',
        journeyNavTitle: 'مخطط الوحدات',
        journeyNavBody: 'انقر هنا لفتح مخطط الوحدات.',
        journeyCtaTitle: 'أضف وحدتك الأولى',
        journeyCtaBody:
          'أنشئ وحدتك الأولى من هنا — أضف فيديوهات ومستندات واختبارًا بالترتيب الذي تريده.',
        usersColumnTitle: 'تتبع التأهيل',
        usersColumnBody: 'تابع تأهيل كل متدرب وموظف جديد خطوة بخطوة.',
        widgetTitle: 'نظرة عامة على التأهيل',
        widgetBody:
          'اطّلع على من أنهى، ومن لا يزال قيد التقدم، وأين يتعثر الأشخاص.',
        finishTitle: 'أنت جاهز',
        finishBody: 'أنت جاهز — أنشئ وحدتك الأولى.',
      },
    },
    internalTeam: {
      tabs: {
        overview: 'نظرة عامة',
        engagement: 'التفاعل',
      },
      empty: 'لا يوجد شيء هنا بعد.',
      comingSoon:
        'قريباً — هذه المنطقة قيد التطوير النشط وسيتم تحسينها باستمرار.',
      modulesAtAGlance: '{{coursesText}} في لمحة',
      totalModules: 'إجمالي {{coursesText}}',
      totalLearners: 'إجمالي {{studentsText}}',
      loadFailed: 'فشل تحميل {{coursesText}}.',
      retry: 'إعادة المحاولة',
      engagement: {
        title: 'تفاعل {{coursesText}}',
        totalEnrollees: 'إجمالي الاشتراكات',
        completedCourses: '{{coursesText}} المكتملة',
        averagePerUser: 'متوسط {{coursesText}} لكل {{studentText}}',
        topCompleted: 'أكثر 5 {{coursesText, removeTheForArabic}} اكتمالاً',
        mostDroppedOff: '{{courseText, addTheForArabic}} الأكثر تسرباً',
        columns: {
          completed: 'مكتمل',
          rate: 'نسبة الإكمال',
        },
        inProgressOfEnrolled: '{{count}} من {{total}} قيد التقدم',
        // KP-60 — the enrollment-status donut.
        statusDistribution: {
          title: 'توزيع حالات الاشتراك',
          subtitle: 'اشتراك',
          notStarted: 'لم يبدأ',
          inProgress: 'قيد التقدم',
          completed: 'مكتمل',
          failed: 'راسب',
        },
        noDropOff: 'لا توجد {{coursesText}} بها متعلمون قيد التقدم حالياً.',
        dropOffDefinition:
          'التسرب = المتعلمون الذين بدأوا {{courseText}} ولم يكملوها (قيد التقدم ÷ إجمالي المسجلين)',
        notAvailable: 'غير متاح',
        drawer: {
          // KP-54 — تسمية النطاق عند شمول جميع الوحدات في التفصيل.
          scopeAllModules: 'جميع {{coursesText}}',
          title: 'المتعلمون — {{courseName}}',
          loadFailed: 'فشل تحميل المتعلمين.',
          retry: 'إعادة المحاولة',
          empty: 'لا يوجد متعلمون يطابقون هذا العرض.',
          close: 'إغلاق',
          columns: {
            name: 'الاسم',
            email: 'البريد الإلكتروني',
            status: 'الحالة',
            progress: 'التقدم',
            lastActivity: 'آخر نشاط',
          },
          statuses: {
            NOT_STARTED: 'لم يبدأ',
            IN_PROGRESS: 'قيد التقدم',
            PASS_WITH_CERTIFICATE: 'مكتمل',
            PASS_WITHOUT_CERTIFICATE: 'مكتمل',
            FAILED: 'راسب',
          },
          neverActive: 'أبداً',
        },
      },
    },
    onboardingSteps: {
      pageTitle: 'خطوات التأهيل',
      loadFailed: 'فشل تحميل خطوات التأهيل.',
      retry: 'إعادة المحاولة',
      empty: 'لا توجد خطوات تأهيل بعد.',
      addStep: 'إضافة خطوة',
      columnOrder: 'الترتيب',
      columnKey: 'المفتاح',
      columnType: 'النوع',
      columnActive: 'مفعّلة',
      columnSkippable: 'قابلة للتخطي',
      columnActions: 'إجراءات',
      edit: 'تعديل',
      save: 'حفظ',
      cancel: 'إلغاء',
      editTitle: 'تعديل الخطوة',
      createTitle: 'إضافة خطوة',
      keyLabel: 'المفتاح',
      keyHelper: 'معرّف قصير وفريد يمكن قراءته آلياً (مثال: welcome-video).',
      typeLabel: 'النوع',
      roleFilterLabel: 'تصفية حسب الدور (اختياري)',
      roleFilterHelper:
        'اتركه فارغاً ليُطبّق على جميع الأدوار. اضغط Enter لإضافة دور.',
      videoTitleEn: 'العنوان (بالإنجليزية)',
      videoTitleAr: 'العنوان (بالعربية)',
      videoUrl: 'رابط الفيديو',
      videoDurationHintEn: 'مدة الفيديو التقريبية (بالإنجليزية)',
      videoDurationHintAr: 'مدة الفيديو التقريبية (بالعربية)',
      videoDisclaimerEn: 'إخلاء المسؤولية (بالإنجليزية)',
      videoDisclaimerAr: 'إخلاء المسؤولية (بالعربية)',
      departments: 'الأقسام',
      addDepartment: 'إضافة قسم',
      departmentKey: 'المفتاح',
      departmentNameEn: 'الاسم (بالإنجليزية)',
      departmentNameAr: 'الاسم (بالعربية)',
      departmentVideoUrl: 'رابط الفيديو',
      removeDepartment: 'إزالة القسم',
      cards: 'البطاقات',
      addCard: 'إضافة بطاقة',
      cardTitleEn: 'عنوان البطاقة (بالإنجليزية)',
      cardTitleAr: 'عنوان البطاقة (بالعربية)',
      cardBodyEn: 'نص البطاقة (بالإنجليزية)',
      cardBodyAr: 'نص البطاقة (بالعربية)',
      removeCard: 'إزالة البطاقة',
      requiresSignature: 'يتطلب توقيعاً',
      groups: 'المجموعات',
      addGroup: 'إضافة مجموعة',
      groupNameEn: 'اسم المجموعة (بالإنجليزية)',
      groupNameAr: 'اسم المجموعة (بالعربية)',
      removeGroup: 'إزالة المجموعة',
      requiresAcceptancePerGroup: 'يتطلب موافقة لكل مجموعة',
      passMark: 'درجة النجاح',
      examId: 'معرّف الاختبار',
      examIdHelper: 'اختياري — المعرّف الرقمي للاختبار/التقييم المرتبط.',
      jsonConfig: 'الإعدادات (JSON)',
      jsonInvalid: 'صيغة JSON غير صحيحة.',
      jsonHelper:
        'لا يوجد نموذج مخصص لهذا النوع بعد — عدّل إعداداته الخام بصيغة JSON.',
      gradingEnabled: 'اختبار مُقيَّم (يتطلب درجة نجاح)',
      gradingPassMark: 'درجة النجاح (%)',
      gradingHelper:
        'يجب أن يحصل المتعلم على هذه النسبة على الأقل لإتمام الخطوة. يمكن إعادة المحاولة دائماً.',
      assessments: 'الأسئلة (التقييمات)',
      assessmentsHelper: 'اختر التقييمات التي تتكوّن منها هذه الخطوة بالترتيب.',
      assessmentsLoadFailed: 'فشل تحميل التقييمات.',
      correctAnswers: 'الإجابات الصحيحة',
      correctAnswersHelper:
        'حدد الخيار الصحيح (أو الخيارات) لكل تقييم — يتم تصحيح الإجابات على الخادم بناءً عليها.',
      noKeyMarked: 'لم يتم تحديد خيار صحيح — لن يُحتسب هذا السؤال في التقييم.',
      saveAnswerKey: 'حفظ الإجابات الصحيحة',
      answerKeySaved: 'تم حفظ الإجابات الصحيحة.',
      answerKeySaveFailed: 'فشل حفظ الإجابات الصحيحة.',
      journeyChecklist: 'الرحلة',
      journeyChecklistHelper:
        'الرحلة (قائمة المهام) المضمّنة في هذه الخطوة — تكتمل الخطوة فقط عند اكتمال الرحلة للمتعلم.',
      journeysLoadFailed: 'فشل تحميل الرحلات.',
      // KP-54 — ربط الاختبار عبر اختياره من قائمة اختبارات الدورة.
      quizCourse: 'الدورة',
      quizCourseHelper: 'اختر الدورة التي تريد الربط بأحد اختباراتها.',
      quizExam: 'الاختبار',
      quizExamHelper:
        'تُعرض فقط الاختبارات المُنشأة عبر منشئ الاختبارات في الدورة.',
      quizzesLoadFailed: 'فشل تحميل الاختبارات.',
      quizzesEmpty:
        'لا توجد اختبارات في هذه الدورة بعد — أنشئ اختباراً عبر منشئ الاختبارات أولاً.',
      coursesLoadFailed: 'فشل تحميل الدورات.',
      quizCurrentExam: 'معرّف الاختبار المرتبط حالياً: {{examId}}.',
      // KP-54 — تسميات الأسئلة (خطوات التقييم).
      questionLabels: 'تسميات الأسئلة',
      questionLabelsHelper:
        'تسمية اختيارية تظهر فوق كل سؤال — أدخلها بالإنجليزية والعربية.',
      questionLabelEn: 'التسمية (بالإنجليزية)',
      questionLabelAr: 'التسمية (بالعربية)',
      // KP-57 — التأليف بمساعدة الذكاء الاصطناعي.
      copyPrompt: 'نسخ موجّه التأليف بالذكاء الاصطناعي',
      promptCopied: 'تم نسخ موجّه التأليف إلى الحافظة.',
      pasteJson: 'لصق JSON',
      pasteJsonTitle: 'لصق إعدادات الخطوة بصيغة JSON',
      pasteJsonHelper:
        'الصق ناتج JSON من موجّه التأليف. يقوم فقط بتعبئة النموذج أدناه — راجعه ثم احفظ.',
      pasteJsonLabel: 'إعدادات JSON',
      pasteApply: 'تعبئة النموذج',
      pasteInvalidJson:
        'صيغة JSON غير صحيحة — الصق كائن JSON الخام فقط، دون أسوار markdown أو نص إضافي.',
      pasteWrongShape:
        'صيغة JSON صحيحة لكنها لا تطابق إعدادات خطوة {{type}}.',
      configApplied: 'تمت تعبئة النموذج بالإعدادات — راجعها ثم احفظ.',
    },
    userResults: {
      tab: 'النتائج',
      loadFailed: 'فشل تحميل النتائج.',
      retry: 'إعادة المحاولة',
      empty: 'لا توجد اشتراكات في {{coursesText}} بعد.',
      notAvailable: 'غير متاح',
      notGradedYet: 'لم يتم التقييم بعد',
      neverActive: 'أبداً',
      columns: {
        module: 'التدريب',
        status: 'الحالة',
        progress: 'التقدم',
        score: 'الدرجة',
        lastActivity: 'آخر نشاط',
      },
      statuses: {
        NOT_STARTED: 'لم يبدأ',
        IN_PROGRESS: 'قيد التقدم',
        PASS_WITH_CERTIFICATE: 'مكتمل • بشهادة',
        PASS_WITHOUT_CERTIFICATE: 'مكتمل',
        FAILED: 'راسب',
      },
    },
    supportTickets: {
      detailsTitle: 'تفاصيل التذكرة',
      unknown: 'غير معروف',
      dueDate: 'تاريخ الاستحقاق',
      statusLabel: 'تحديث الحالة',
      updateStatus: 'تحديث',
      assignees: 'المكلفون',
      description: 'الوصف',
      addComment: 'إضافة تعليق',
      submitComment: 'إرسال',
      comments: 'التعليقات',
      noComments: 'لا توجد تعليقات بعد.',
      unknownAuthor: 'كاتب غير معروف',
      noTaskSelected: 'اختر تذكرة لعرض تفاصيلها.',
      noListsConfigured: 'لا توجد قوائم ClickUp مهيأة لهذه المنظمة.',
      retry: 'إعادة المحاولة',
      tasksCount: '{{count}} مهمة',
      completedTasksCount: '{{count}} مكتملة',
      showCompletedTasks: 'عرض المهام المكتملة',
      showTasks: 'عرض المهام',
      noTasks: 'لا توجد مهام في هذه القائمة.',
      noCompletedTasks: 'لا توجد مهام مكتملة.',
      toggleShowCompleted: 'عرض المهام المكتملة',
      createdAt: 'تاريخ الإنشاء',
      updatedAt: 'آخر تحديث',
      completed: 'مكتملة',
      closed: 'مغلقة',
      loadMore: 'تحميل 10 إضافية',
    },
    watarRoles: {
      title: 'الأدوار المبسّطة',
      description:
        'تستخدم مساحة العمل هذه أربعة أدوار: مدير وتر، منضم جديد، متدرب، وموظف وتر. أنشئ أي دور مفقود وطبّق صلاحياته الأساسية — لن تُحذف الأدوار الموجودة أو الصلاحيات المخصصة أبداً.',
      setupRoles: 'إعداد الأدوار',
      syncRoles: 'إعادة تطبيق الأساسيات',
      done: 'الأدوار جاهزة.',
      failed: 'فشل إعداد الأدوار — يرجى المحاولة مرة أخرى.',
    },
    // KP-56/KP-59 — AI Assistant block (keep as ONE delimited subtree)
    aiAssistant: {
      settings: {
        title: 'المساعد الذكي',
        description:
          'اربط مزوّد الإكمال الخاص بالمنظمة. يُخزَّن المفتاح مشفّراً ويُعرض دائماً بشكل مقنّع — ولا يُعاد إرساله إلى المتصفح أبداً.',
        provider: 'المزوّد',
        openAiCompatible: 'نقطة نهاية متوافقة مع OpenAI',
        apiKey: 'مفتاح الواجهة البرمجية',
        baseUrl: 'الرابط الأساسي',
        model: 'النموذج',
        modelOptional: 'اختياري — اتركه فارغاً لاستخدام النموذج الافتراضي.',
        configured: 'مُهيّأ • {{keyMasked}}',
        save: 'حفظ',
        clear: 'إزالة',
        cancel: 'إلغاء',
        clearConfirmTitle: 'إزالة المفتاح المخزَّن؟',
        clearConfirmBody:
          'ستتوقف ميزات المساعد المعتمدة على هذا المزوّد عن العمل لهذه المنظمة حتى يتم حفظ مفتاح جديد.',
        errors: {
          required: 'هذا الحقل مطلوب.',
          tooShort: 'يبدو المفتاح قصيراً جداً.',
        },
      },
      insights: {
        title: 'رؤى',
        generate: 'توليد الرؤى',
        refresh: 'تحديث الرؤى',
        intro:
          'احصل على ملخص سريع لأبرز مؤشرات إكمال الوحدات ونقاط التسرب — يُحسب عند الطلب من بيانات لوحة المعلومات.',
        caption: 'محسوبة من بيانات الإكمال والتسرب الحالية في لوحة المعلومات.',
        loadFailed: 'تعذّر حساب الرؤى — يرجى المحاولة مرة أخرى.',
        empty:
          'لا توجد بيانات كافية بعد — تظهر الرؤى بعد إسناد الوحدات إلى المتعلمين.',
        highlights: {
          overall: '{{modules}} وحدات نشطة — نسبة الإكمال الإجمالية {{percent}}%.',
          atRisk:
            'الأكثر خطورة: "{{name}}" بنسبة إكمال {{percent}}% من بين {{assigned}} متعلماً مسنداً.',
          biggestDrop:
            'أكبر تسرب في "{{name}}": يتوقف {{count}} متعلماً عند "{{step}}".',
          best: 'الأفضل أداءً: "{{name}}" بنسبة إكمال {{percent}}%.',
        },
      },
    },
  };
  const i18nOptions: InitOptions = {
    lng: initialLanguage,
    fallbackLng: LocaleEnum.ENGLISH,
    preload: [LocaleEnum.ENGLISH, LocaleEnum.ARABIC],
    initImmediate: true,
    resources: {
      en: { translation: defaultTranslations },
      ar: { translation: arDefaultTranslations },
    },
    interpolation: {
      format: (value, format, lng) => {
        if (!value) return;
        if (format === 'lowercase') {
          return value.toLowerCase();
        }
        if (format === 'firstLetterUpperCase') {
          return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
        }
        if (format === 'removeTheForArabic' && lng === 'ar') {
          return value.startsWith('ال') ? value.slice(2) : value;
        }
        if (format === 'addTheForArabic' && lng === 'ar') {
          return value.startsWith('ال') ? value : `ال${value}`;
        }
        return value;
      },
    },
  };

  await i18next.init(i18nOptions);
  return i18next;
};
